/**
 * Data shape adapters: transform our Next.js API response shapes
 * into the format that TimedTicketingUI components expect.
 */
import { getTicketSaleStatus, type TicketSaleStatus } from '@/lib/ticket-sale-status'

// Our API shapes
export interface DayTabConfig {
  enabled: boolean
  tabs: Array<{ label: string; ticketTypeIds: string[] }>
}

interface OurEvent {
  id: string
  name: string
  slug: string
  description?: string | null
  startDate: string
  endDate?: string | null
  timezone?: string | null
  status: string
  eventType?: string | null
  isFeatured?: boolean
  dayTabConfig?: DayTabConfig | null
  imageUrl?: string | null
  imageFitMode?: string | null
  bannerDisplayMode?: string | null
  bannerUrl?: string | null
  squareImageUrl?: string | null
  videoUrl?: string | null
  videoEmbedOptions?: { autoplay?: boolean; mute?: boolean; loop?: boolean; controls?: boolean; startTime?: number } | null
  presaleEnabled?: boolean
  presaleStartDate?: string | null
  presaleEndDate?: string | null
  publicSaleStartDate?: string | null
  saleEndDate?: string | null
  venue?: {
    id: string
    name: string
    address?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
    capacity?: number | null
    latitude?: number | null
    longitude?: number | null
  } | null
  ticketTypes?: OurTicketType[]
  createdAt?: string
  updatedAt?: string
}

interface OurTicketType {
  id: string
  name: string
  description?: string | null
  price: number
  allInPrice?: number
  quantity: number
  sold: number
  reserved?: number
  isVisible?: boolean
  isAddon?: boolean
  minQuantity?: number | null
  maxQuantity?: number | null
  /** Most one buyer may hold of this type for the event; null = no limit. */
  maxPerCustomer?: number | null
  /** Units the signed-in buyer already holds (lib/checkout/max-per-customer). */
  perCustomerAlready?: number | null
  quantityInterval?: number | null
  isFeature?: boolean
  badgeText?: string | null
  includesPerks?: string[] | null
  backgroundColor?: string | null
  textColor?: string | null
  sortOrder?: number
  showRemainingQuantity?: boolean
  applicableDates?: string[]
  maxScans?: number | null
  issuePerDay?: boolean
  accessTo?: string | null
  memberOnly?: boolean
  tagVisibility?: string
  // Per-ticket sale window
  presaleStartDate?: string | null
  presaleEndDate?: string | null
  publicSaleStartDate?: string | null
  saleEndDate?: string | null
  saleStatus?: TicketSaleStatus
}

// TimedTicketingUI shapes
export interface TimedUIEvent {
  id: string
  title: string
  name: string
  slug: string
  description: string
  start: number
  startDate: string
  endDate: string | null
  /** IANA timezone of the event (e.g. "Australia/Sydney"); used to render dates/times in the event's local time. */
  timezone: string
  status: string
  eventType: string
  smallPic: string
  largePic: string
  imageUrl: string
  imageFitMode: string
  bannerDisplayMode: string
  bannerUrl: string
  squareImageUrl: string
  venue: {
    id: string
    name: string
    address: string
    city: string
    state: string
    zipCode: string
    capacity: number
    latitude?: number
    longitude?: number
  } | null
  venueCity: string
  ticketTypes: TimedUITicketType[]
  onSale: boolean
  waveTimes: unknown[]
  isSeries: boolean
  videoUrl: string
  videoEmbedOptions?: { autoplay?: boolean; mute?: boolean; loop?: boolean; controls?: boolean; startTime?: number } | null
  presaleEnabled: boolean
  presaleStartDate: string | null
  presaleEndDate: string | null
  publicSaleStartDate: string | null
  saleEndDate: string | null
  isFeatured: boolean
  dayTabConfig: DayTabConfig | null
}

export interface TimedUITicketType {
  id: string
  name: string
  description: string
  price: number
  allInPrice: number
  quantity: number
  sold: number
  reserved: number
  available: number
  isVisible: boolean
  isAvailable: boolean
  isAddon: boolean
  minQuantity: number
  maxQuantity: number
  /** Most one buyer may hold; null = no limit. */
  maxPerCustomer: number | null
  /** What the signed-in buyer already holds of this type (0 when unknown). */
  perCustomerAlready: number
  quantityInterval: number
  isFeature: boolean
  badgeText: string | null
  /** What the ticket includes, listed on its card (empty = nothing listed). */
  includesPerks: string[]
  backgroundColor: string | null
  textColor: string | null
  sortOrder: number
  showRemainingQuantity: boolean
  applicableDates: string[]
  maxScans: number | null
  /** 2+ dates issued as one ticket per day (lib/tickets/day-tickets). */
  issuePerDay: boolean
  accessTo: string | null
  memberLocked: boolean
  originalAllInPrice: number | null
  discountedAllInPrice: number | null
  /** 'on_sale' | 'sale_ended' | 'not_yet' — gates purchasability + UI state. */
  saleStatus: TicketSaleStatus
}

export function adaptEventForUI(event: OurEvent): TimedUIEvent {
  const startTimestamp = Math.floor(new Date(event.startDate).getTime() / 1000)
  const imageUrl = event.imageUrl || ''
  const bannerUrl = event.bannerUrl || imageUrl
  const squareImageUrl = event.squareImageUrl || imageUrl

  return {
    id: event.id,
    title: event.name,
    name: event.name,
    slug: event.slug,
    description: event.description || '',
    start: startTimestamp,
    startDate: event.startDate,
    endDate: event.endDate || null,
    timezone: event.timezone || 'America/New_York',
    status: event.status,
    eventType: event.eventType || 'GENERAL_ADMISSION',
    smallPic: squareImageUrl,
    largePic: bannerUrl,
    imageUrl,
    imageFitMode: event.imageFitMode || 'cover',
    bannerDisplayMode: event.bannerDisplayMode || 'standard',
    bannerUrl,
    squareImageUrl,
    videoUrl: event.videoUrl || '',
    videoEmbedOptions: event.videoEmbedOptions || null,
    venue: event.venue ? {
      id: event.venue.id,
      name: event.venue.name,
      address: event.venue.address || '',
      city: event.venue.city || '',
      state: event.venue.state || '',
      zipCode: event.venue.zipCode || '',
      capacity: event.venue.capacity || 0,
      latitude: event.venue.latitude || undefined,
      longitude: event.venue.longitude || undefined,
    } : null,
    venueCity: event.venue ? `${event.venue.city || ''}, ${event.venue.state || ''}`.replace(/^, |, $/, '') : '',
    ticketTypes: (event.ticketTypes || []).map(adaptTicketTypeForUI),
    onSale: event.status === 'ON_SALE',
    waveTimes: [],
    isSeries: false,
    presaleEnabled: event.presaleEnabled || false,
    presaleStartDate: event.presaleStartDate || null,
    presaleEndDate: event.presaleEndDate || null,
    publicSaleStartDate: event.publicSaleStartDate || null,
    saleEndDate: event.saleEndDate || null,
    isFeatured: event.isFeatured || false,
    dayTabConfig: event.dayTabConfig || null,
  }
}

export function adaptTicketTypeForUI(tt: OurTicketType): TimedUITicketType {
  const reserved = tt.reserved || 0
  const available = Math.max(0, tt.quantity - tt.sold - reserved)
  // Prefer the server-computed status (avoids SSR/client drift); fall back to
  // computing from the ticket's own sale window.
  const saleStatus: TicketSaleStatus = tt.saleStatus || getTicketSaleStatus(tt)
  const onSale = saleStatus === 'on_sale'

  return {
    id: tt.id,
    name: tt.name,
    description: tt.description || '',
    price: tt.price,
    allInPrice: tt.allInPrice || tt.price,
    quantity: tt.quantity,
    sold: tt.sold,
    reserved,
    available,
    isVisible: tt.isVisible !== false,
    isAvailable: available > 0 && tt.isVisible !== false && onSale,
    isAddon: tt.isAddon || false,
    minQuantity: tt.minQuantity || 1,
    maxQuantity: tt.maxQuantity || 10,
    maxPerCustomer: typeof tt.maxPerCustomer === 'number' && tt.maxPerCustomer > 0 ? tt.maxPerCustomer : null,
    perCustomerAlready: Math.max(0, Number(tt.perCustomerAlready || 0)),
    quantityInterval: tt.quantityInterval || 1,
    isFeature: tt.isFeature || false,
    badgeText: tt.badgeText || null,
    includesPerks: Array.isArray(tt.includesPerks) ? tt.includesPerks.filter(Boolean).slice(0, 6) : [],
    backgroundColor: tt.backgroundColor || null,
    textColor: tt.textColor || null,
    sortOrder: tt.sortOrder || 0,
    showRemainingQuantity: tt.showRemainingQuantity !== false,
    applicableDates: tt.applicableDates || [],
    maxScans: tt.maxScans ?? null,
    issuePerDay: !!tt.issuePerDay,
    accessTo: tt.accessTo || null,
    memberLocked: !!(tt as any)._memberLocked || !!(tt.memberOnly && (tt.tagVisibility || 'hidden') === 'locked'),
    originalAllInPrice: (tt as any)._originalAllInPrice ?? null,
    discountedAllInPrice: (tt as any)._discountedAllInPrice ?? null,
    saleStatus,
  }
}

export function adaptEventsListForUI(events: OurEvent[]): TimedUIEvent[] {
  return events.map(adaptEventForUI)
}

// --- Timed Ticketing Adapters ---

export interface DateEvent {
  id: string
  start: number
  startDate: string
  sold?: string
  availableQuantity?: number
  quantity?: number
}

export interface WaveTimeSlot {
  id: string
  waveTime: string | number
  sold?: string
  availableQuantity?: number
  quantity?: number
}

interface ScheduleData {
  id: string
  startDate: string
  endDate: string
  recurrenceRule: string | null
  timeSlots: Array<{ capacity: number; dayOfWeek: number | null }>
  blackoutDates: Array<{ date: string }>
}

const RRULE_DAY_MAP: Record<string, number> = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 }

/**
 * Generate available dates from schedule data for DatePillStrip.
 * Iterates from schedule start to end, filtering by recurrence rule and blackout dates.
 */
export function generateAvailableDates(schedules: ScheduleData[]): DateEvent[] {
  const dates: DateEvent[] = []
  const blackoutSet = new Set<string>()

  for (const schedule of schedules) {
    for (const bd of schedule.blackoutDates) {
      // Normalize blackout dates to YYYY-MM-DD using UTC parts for ISO strings
      const bdVal = bd.date
      if (typeof bdVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bdVal)) {
        blackoutSet.add(bdVal)
      } else {
        const d = new Date(bdVal)
        blackoutSet.add(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`)
      }
    }

    let allowedDays: number[] | null = null
    if (schedule.recurrenceRule) {
      const match = schedule.recurrenceRule.match(/BYDAY=([A-Z,]+)/)
      if (match) {
        allowedDays = match[1].split(',').map(d => RRULE_DAY_MAP[d]).filter(d => d !== undefined)
      }
    }

    // Parse start/end as YYYY-MM-DD strings to avoid UTC midnight timezone shift
    const startStr = typeof schedule.startDate === 'string' ? schedule.startDate.split('T')[0] : new Date(schedule.startDate).toISOString().split('T')[0]
    const endStr = typeof schedule.endDate === 'string' ? schedule.endDate.split('T')[0] : new Date(schedule.endDate).toISOString().split('T')[0]
    const [sy, sm, sd] = startStr.split('-').map(Number)
    const [ey, em, ed] = endStr.split('-').map(Number)
    const end = new Date(ey, em - 1, ed, 12, 0, 0)
    const current = new Date(sy, sm - 1, sd, 12, 0, 0)

    while (current <= end) {
      // Use local date parts (noon local time avoids rollover)
      const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`
      const dayOfWeek = current.getDay()

      if ((!allowedDays || allowedDays.includes(dayOfWeek)) && !blackoutSet.has(dateStr)) {
        const dayCapacity = schedule.timeSlots
          .filter(ts => ts.dayOfWeek === null || ts.dayOfWeek === dayOfWeek)
          .reduce((sum, ts) => sum + ts.capacity, 0)

        dates.push({
          id: `${schedule.id}-${dateStr}`,
          start: Math.floor(current.getTime() / 1000),
          startDate: dateStr,
          sold: '0',
          availableQuantity: dayCapacity,
          quantity: dayCapacity,
        })
      }

      current.setDate(current.getDate() + 1)
    }
  }

  return dates.sort((a, b) => a.start - b.start)
}

/**
 * Convert available-slots API response to WaveTimeSlot[] for TimeSlotGrid.
 * TimeSlotGrid's formatTimeLabel handles "HH:MM" strings directly.
 */
export function adaptSlotsToWaveTimeSlots(
  slots: Array<{
    id: string
    startTime: string
    endTime: string
    capacity: number
    remaining: number
    price: number | null
    label: string | null
  }>
): WaveTimeSlot[] {
  return slots.map(slot => ({
    id: slot.id,
    waveTime: slot.startTime,
    sold: slot.remaining <= 0 ? '1' : '0',
    availableQuantity: slot.remaining,
    quantity: slot.capacity,
  }))
}

/**
 * The most a buyer may pick of a type in one go: the per-order maximum,
 * capped by what is left of the per-customer limit. Never NaN; 0 when the
 * buyer already holds the limit.
 */
export function pickerMax(t: Pick<TimedUITicketType, 'maxQuantity' | 'maxPerCustomer' | 'perCustomerAlready'>): number {
  const perOrder = t.maxQuantity || 10
  if (t.maxPerCustomer == null) return perOrder
  return Math.max(0, Math.min(perOrder, t.maxPerCustomer - (t.perCustomerAlready || 0)))
}
